import MapIcon from "@material-ui/icons/Map";
import StarRateIcon from "@material-ui/icons/StarRate";
import CallSharpIcon from "@material-ui/icons/CallSharp";
import VpnKeySharpIcon from "@material-ui/icons/VpnKeySharp";
import TooltipBar from "components/Tooltip";
import GeolocationStatusIndicator from "components/Shared/GeolocationStatusIndicator";
import Tooltip from "@material-ui/core/Tooltip";
import { withStyles } from "@material-ui/core/styles";
import EmptyCircleIcon from "@material-ui/icons/RadioButtonUnchecked";
import FilledCircleIcon from "@material-ui/icons/RadioButtonChecked";
import DocumentIcon from "@material-ui/icons/Description";
import { IconButton } from "@material-ui/core";
import moment from "moment";
import { PATHS } from "util/appConstants";
import { getTourDeliveryTimings } from "util/helpers";
import { isHandoverOrder } from "util/handoverStatusUtils";
import { formatRouteStopCount } from "util/stopLabelUtils";
import { UndoSharp } from "@material-ui/icons";
import { useDispatch } from "react-redux";
import EditIcon from "@material-ui/icons/Edit";
import ErrorIcon from '@mui/icons-material/Error';
import HandshakeIcon from "@mui/icons-material/Handshake";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import { formatDeparture } from "util/departureDisplay";

export function canShowRouteUndoButton(user) {
  return user?.permissions?.routes?.hide_undo !== true;
}

export function shouldHidePdf(rowData) {
  const pathwayCategory =
    rowData?.pathway?.[0]?.tour_type_category ??
    rowData?.pathway?.find?.((stop) => stop?.tour_type_category)?.tour_type_category;
  const isMiddleMileDigital = rowData?.middle_mile_digital ?? rowData?.Tour?.middle_mile_digital;
  return pathwayCategory === "middle_mile" || pathwayCategory === "pickup" || !!isMiddleMileDigital;
}

const DriverNameTooltip = withStyles({
  tooltip: {
    backgroundColor: "#93B5F0 !important",
    color: "#121212 !important",
    padding: "12px 16px !important",
    borderRadius: "4px !important",
    fontSize: "12px !important",
    fontFamily: "Roboto, sans-serif !important",
    boxShadow: "0px 2px 6px rgba(0,0,0,0.3) !important",
  },
  arrow: {
    color: "#93B5F0 !important",
  },
})(Tooltip);

export const MASTER_ADMIN_DATA_BAR = {
  name: "Master Data",
  list: [
    {
      name: "Customer Archive",
      path: PATHS.customers.archive,
      permission: (user) => user?.permissions?.customers?.archive,
    },
    {
      name: "Customers",
      path: PATHS.customers.root,
      permission: (user) => user?.permissions?.customers?.list,
    },
    {
      name: "Tours",
      path: PATHS.tours.root,
      permission: (user) => user?.permissions?.tours?.list,
    },
    {
      name: "Zipcodes",
      path: PATHS.zipcodes.root,
      permission: (user) => user?.permissions?.zipcodes?.list,
    },
    {
      name: "Visualization",
      path: PATHS.zipcodes.visualization,
      permission: (user) => user?.permissions?.zipcodes?.visualization,
    },
  ],
};

export const TOUR_DATA_BAR = {
  name: "Routes",
  list: [
    {
      name: "Current routes",
      path: PATHS.routes.current,
      permission: (user) => user?.permissions?.routesList || user?.permissions?.routes?.list,
    },
    {
      name: "Recently finished routes",
      path: PATHS.routes.recent,
      permission: (user) => user?.permissions?.routes?.recently_finished_routes,
    },
    {
      name: "Archived routes",
      path: PATHS.routes.archive,
      permission: (user) => user?.permissions?.routes?.archive,
    },
    {
      name: "Export",
      path: PATHS.routes.export,
      permission: (user) => user?.permissions?.routes?.export,
    },
  ],
};
export const HANDOVER_DATA_BAR = {
  name: "Handover Protocols",
  list: [
    {
      name: "Open Handovers",
      path: PATHS.handovers.root,
      permission: (user) => user?.permissions?.handovers?.list,
    },
    {
      name: "Completed Handovers",
      path: PATHS.handovers.completed,
      permission: (user) => user?.permissions?.handovers?.list,
    },
  ],
};
export const ADMIN_DATA_BAR = {
  name: "Admin",
  list: [
    {
      name: "General Settings",
      path: PATHS.admin.accountPermissions,
      permission: (user) => user?.permissions?.admin?.account_permissions
    },
    {
      name: "Suppliers",
      path: PATHS.admin.suppliers,
      permission: (user) => user?.permissions?.admin?.supplier,
    },
    {
      name: "Supplier Locations",
      path: PATHS.admin.supplierLocations,
      permission: (user) => user?.permissions?.admin?.supplier_locations || user?.isSuperAdmin,
    },
    {
      name: "Admins",
      path: PATHS.admin.supplierLocations,
      permission: (user) => user?.isSuperAdmin,
    },
    {
      name: "Users",
      path: PATHS.admin.users,
      permission: (user) => user?.permissions?.user?.list
    },
  ],
};
export const DIAGNOSTICS_DATA_BAR = {
  name: "Diagnostics",
  list: [
    {
      name: "Web",
      path: PATHS.diagnostics.web,
      permission: (user) =>
        user?.isSuperAdmin && import.meta.env.VITE_SHOW_SUPPORT === "true",
    },
    {
      name: "Mobile",
      path: PATHS.diagnostics.mobile,
      permission: (user) =>
        user?.isSuperAdmin && import.meta.env.VITE_SHOW_SUPPORT === "true",
    },
  ],
};
export const NAVIGATION_ROUTES = [
  {
    name: "Account Settings",
    path: PATHS?.admin?.users,
    permission: (permissions) => permissions?.admin?.account_permissions || permissions?.admin?.supplier || permissions?.admin?.supplier_locations || permissions?.user?.list
  },
  {
    name: "Routes",
    path: PATHS.routes.export,
    permission: (permissions) => permissions?.routes?.export && !permissions?.routes?.list,
  },
  {
    name: "Routes",
    path: PATHS.routes.current,
    permission: (permissions) => permissions?.routesList || permissions?.routes?.list,
  },
  {
    name: "Handover Protocols",
    path: PATHS.handovers.root,
    permission: (permissions) => permissions?.handovers?.list,
  },
  {
    name: "Orders",
    path: PATHS.orders.root,
    permission: (permissions) => permissions?.ordersList || permissions?.orders?.list,
  },
  {
    name: "Past Deliveries",
    path: PATHS.pastdeliveries,
    permission: (permissions) => permissions?.routes?.past_deliveries,
  },
  {
    name: "Delivery History",
    path: PATHS.deliveryhistory,
    permission: (permissions) => permissions?.routes?.delivery_history,
  },
  {
    name: "Master Data",
    path: PATHS.customers.root,
    permission: (permissions) =>
      permissions?.customers?.list || permissions?.tours?.list || permissions?.zipcodes?.list,
  },
  {
    name: "Map",
    path: PATHS.tours.map,
    permission: (permissions) => permissions?.routesMap || permissions?.routes?.map,
  },
  {
    name: "Accounts",
    path: PATHS.admin.supplierGroups,
    permission: (permissions) => permissions?.admin?.supplier_groups,
  },
  {
    name: "Roles",
    path: PATHS.admin.roles,
    permission: (permissions) => permissions?.admin?.roles,
  }, {
    name: "Permissions",
    path: PATHS.admin.permissions,
    permission: (permissions) => permissions?.admin?.permissions,
  },
  {
    name: "Diagnostics",
    path: PATHS.diagnostics.web,
    permission: (_permissions, user) =>
      user?.isSuperAdmin && import.meta.env.VITE_SHOW_SUPPORT === "true",
  },
  { name: "Settings", path: "/dash2" },
];
export const TOURS_TABLE_COLUMNS = (t) => {
  const getTourTypeCategoryLabel = (rowData) => {
    const tourType = rowData?.tour_type_category ?? rowData?.tour_category;
    if (tourType === "pickup") {
      return t("Pickup Tour");
    }
    if (tourType === "delivery") {
      return t("Delivery Tour");
    }
    if (tourType === "mix") {
      return t("Mix Tour");
    }
    if (tourType) {
      return t(tourType);
    }
    return "";
  };

  return [
    { title: "Tour name", field: "name" },
    {
      title: "Tour Type",
      field: "tour_type_category",
      render: (rowData) => getTourTypeCategoryLabel(rowData),
      customFilterAndSearch: (query, rowData) => {
        const tourType = rowData?.tour_type_category ?? rowData?.tour_category;
        const haystack = [tourType || "", getTourTypeCategoryLabel(rowData)].join(" ").toUpperCase();
        return haystack.includes(query.trim().toUpperCase());
      },
    },
    { title: "Tour ID", field: "user_defined_tour_id" },
    { title: "Active", field: "active", type: "boolean" },
    { title: "Default Tour", field: "is_default", type: "boolean" },
    { title: "Third party", field: "third_party", type: "boolean" },
    {
      title: "Delivery Timings",
      field: "delivery_timings",
      render: (rowData) => {
        const timings = getTourDeliveryTimings(rowData);
        return timings.length
          ? timings.map((deliveryTiming) => t(deliveryTiming)).join(", ")
          : "";
      },
      customFilterAndSearch: (query, rowData) => {
        const timings = getTourDeliveryTimings(rowData);
        if (!timings.length) {
          return false;
        }
        const haystack = timings
          .flatMap((timing) => [timing, t(timing)])
          .join(" ")
          .toUpperCase();
        return haystack.includes(query.trim().toUpperCase());
      },
    },
  ];
};

export const SUPPLIER_LOCATIONS_TABLE_COLUMNS = (t) => {
  return [
    { title: "Supplier", field: "Supplier.name" },
    { title: "Supplier Location", field: "name" },
    {
      title: "Street",
      field: "street",
      render: (rowData) => (
        <>
          {rowData.street} {rowData.street_number}
        </>
      ),
    },
    { title: "Zipcode", field: "zipcode" },
    { title: "City", field: "city" },
    { title: "Telephone Number", field: "phone" },
    { title: "E-mail", field: "email" },
  ];
};
export const SUPPLIER_GROUPS_TABLE_COLUMNS = (t) => {
  return [
    { title: "Id", field: "id" },
    { title: "Account", field: "name" },
    {
      title: "Logo",
      field: "filename",
      render: (rowData) => (
        <a src={rowData.image_url}>{rowData.filename}</a>
      ),
    },
  ];
};
export const SUPPLIERS_TABLE_COLUMNS = (t) => {
  return [
    { title: "Id", field: "id" },
    { title: "Supplier", field: "name" },
    { title: "Account", field: "SupplierGroup.name" },
    {
      title: "Street",
      field: "street",
      render: (rowData) => (
        <>
          {rowData.street} {rowData.street_number}
        </>
      ),
    },
    { title: "Zipcode", field: "zipcode" },
    { title: "City", field: "city" },
    { title: "Telephone Number", field: "phone" },
    { title: "E-mail", field: "email" },
    { title: "Active", field: "active", type: "boolean" },

  ];
};
export const ROLES_TABLE_COLUMNS = (t) => {
  return [
    { title: "Id", field: "id" },
    { title: "Name", field: "name" },

  ];
};
export const PERMISSIONS_TABLE_COLUMNS = (t) => {
  return [
    { title: "Id", field: "id" },
    { title: "Name", field: "name" },
    { title: "Module", field: "module" },
    { title: "Default Value", field: "default" },

  ];
};

export const USERS_TABLE_COLUMNS = (t) => {
  return [
    { title: "Id", field: "id" },
    { title: "Name", field: "name" },
    { title: "Surname", field: "surname" },
    { title: "Supplier", field: "Supplier.name" },
    { title: "Supplier Location", field: "SupplierLocation.name" },
    { title: "E-Mail", field: "email" },
    { title: "Role", field: "Role.name" },
    { title: "Active", field: "active", type: "boolean" },

  ];
};

const filterCustomerTour = (query, rowData, t) => {
  if (!query) return true;
  const timings = rowData.CustomerDeliveryTimings || [];
  const tourNamesAndDeps = new Set();
  if (timings.length > 0) {
    for (const cdt of timings) {
      const tdt = cdt.ToursDeliveryTiming;
      if (tdt) {
        const tourName = tdt.Tour?.name;
        if (tourName) tourNamesAndDeps.add(tourName);
        if (tdt.delivery_timing) {
          tourNamesAndDeps.add(tdt.delivery_timing);
          tourNamesAndDeps.add(formatDeparture(tdt.delivery_timing, t));
        }
      }
    }
  }
  if (tourNamesAndDeps.size === 0 && rowData.Tour?.name) {
    tourNamesAndDeps.add(rowData.Tour.name);
    const fallbackDeps = rowData.departure
      ? [rowData.departure]
      : getTourDeliveryTimings(rowData.Tour);
    for (const dep of fallbackDeps) {
      tourNamesAndDeps.add(dep);
      tourNamesAndDeps.add(formatDeparture(dep, t));
    }
  }
  const searchStr = [...tourNamesAndDeps].join(" ").toLowerCase();
  return searchStr.includes(query.trim().toLowerCase());
};

export const CUSTOMERS_TABLE_COLUMNS = (t, user) => {
  const columns = [
    { title: "ID", field: "number" },
    {
      title: "Name",
      field: "name",
      customSort: (a, b) => a?.name?.localeCompare(b?.name),
    },
    {
      title: "Street",
      field: "street",
      render: (rowData) => (
        <>
          {rowData.street} {rowData.street_number}
        </>
      ),
    },
    { title: "Zipcode", field: "zipcode" },
    { title: "City", field: "city" },
    {
      title: "Tour",
      field: "Tour.name",
      customSort: (a, b) => {
        const getTourString = (row) => {
          const timings = row.CustomerDeliveryTimings || [];
          if (timings.length > 0) {
            const names = [];
            for (const cdt of timings) {
              const tourName = cdt.ToursDeliveryTiming?.Tour?.name;
              if (tourName && !names.includes(tourName)) names.push(tourName);
            }
            if (names.length > 0) return names.join(", ");
          }
          return row.Tour?.name || "";
        };
        return getTourString(a).localeCompare(getTourString(b));
      },
      customFilterAndSearch: (query, rowData) => filterCustomerTour(query, rowData, t),
      render: (rowData) => {
        const timings = rowData.CustomerDeliveryTimings || [];
        if (timings.length > 0) {
          const tourMap = new Map();
          for (const cdt of timings) {
            const tdt = cdt.ToursDeliveryTiming;
            if (tdt) {
              const tourName = tdt.Tour?.name || "No Tour";
              if (!tourMap.has(tourName)) {
                tourMap.set(tourName, new Set());
              }
              if (tdt.delivery_timing) {
                tourMap.get(tourName).add(tdt.delivery_timing);
              }
            }
          }
          const entries = [...tourMap.entries()];
          if (entries.length > 0) {
            return (
              <div>
                {entries.map(([name, deps], idx) => {
                  const depText =
                    deps.size > 0
                      ? ` (${[...deps].map((dep) => formatDeparture(dep, t)).join(", ")})`
                      : "";
                  return <div key={idx}>{`${name}${depText}`}</div>;
                })}
              </div>
            );
          }
        }
        if (rowData.Tour?.name) {
          const tourDeps = rowData.departure
            ? [rowData.departure]
            : getTourDeliveryTimings(rowData.Tour);
          const depText =
            tourDeps.length > 0
              ? ` (${tourDeps.map((dep) => formatDeparture(dep, t)).join(", ")})`
              : "";
          return `${rowData.Tour.name}${depText}`;
        }
        return "";
      },
    },
    {
      title: "Express Customer",
      field: "priority",
      type: "boolean",
    },
    {
      title: "Active",
      field: "active",
      type: "boolean",
      default: true,
      defaultSort: "desc",
    },
  ];
  if (user?.permissions?.customers?.show_received_location_and_status) {
    columns.splice(columns.length - 1, 0, {
      title: "Geo Location",
      field: "geo_location_status",
      render: (rowData) => (
        <GeolocationStatusIndicator status={rowData.geo_location_status} />
      ),
      lookup: {
        auto: t("Yellow"),
        manual: t("Green"),
        error: t("Red"),

      },
      customSort: (a, b) => {
        const getPriority = (status) => {
          switch (status) {
            case 'manual': return 1;
            case 'auto': return 2;
            case 'error': return 3;
            default: return 4;
          }
        };
        return getPriority(a.geo_location_status) - getPriority(b.geo_location_status);
      },
    });
  }

  return columns;
};

export const CUSTOMERS_TABLE_COLUMNS_ADMIN = (t, user) => {
  const columns = [
    { title: "ID", field: "number" },
    { title: "Sender", field: "Supplier.name" },
    {
      title: "Name",
      field: "name",
      customSort: (a, b) => a?.name?.localeCompare(b?.name),
    },
    {
      title: "Street",
      field: "street",
      render: (rowData) => (
        <>
          {rowData.street} {rowData.street_number}
        </>
      ),
    },
    { title: "Zipcode", field: "zipcode" },
    { title: "City", field: "city" },
    {
      title: "Tour",
      field: "Tour.name",
      customSort: (a, b) => {
        const getTourString = (row) => {
          const timings = row.CustomerDeliveryTimings || [];
          if (timings.length > 0) {
            const names = [];
            for (const cdt of timings) {
              const tourName = cdt.ToursDeliveryTiming?.Tour?.name;
              if (tourName && !names.includes(tourName)) names.push(tourName);
            }
            if (names.length > 0) return names.join(", ");
          }
          return row.Tour?.name || "";
        };
        return getTourString(a).localeCompare(getTourString(b));
      },
      customFilterAndSearch: (query, rowData) => filterCustomerTour(query, rowData, t),
      render: (rowData) => {
        const timings = rowData.CustomerDeliveryTimings || [];
        if (timings.length > 0) {
          const tourMap = new Map();
          for (const cdt of timings) {
            const tdt = cdt.ToursDeliveryTiming;
            if (tdt) {
              const tourName = tdt.Tour?.name || "No Tour";
              if (!tourMap.has(tourName)) {
                tourMap.set(tourName, new Set());
              }
              if (tdt.delivery_timing) {
                tourMap.get(tourName).add(tdt.delivery_timing);
              }
            }
          }
          const entries = [...tourMap.entries()];
          if (entries.length > 0) {
            return (
              <div>
                {entries.map(([name, deps], idx) => {
                  const depText =
                    deps.size > 0
                      ? ` (${[...deps].map((dep) => formatDeparture(dep, t)).join(", ")})`
                      : "";
                  return <div key={idx}>{`${name}${depText}`}</div>;
                })}
              </div>
            );
          }
        }
        if (rowData.Tour?.name) {
          const tourDeps = rowData.departure
            ? [rowData.departure]
            : getTourDeliveryTimings(rowData.Tour);
          const depText =
            tourDeps.length > 0
              ? ` (${tourDeps.map((dep) => formatDeparture(dep, t)).join(", ")})`
              : "";
          return `${rowData.Tour.name}${depText}`;
        }
        return "";
      },
    },
    {
      title: "Express Customer",
      field: "priority",
      type: "boolean",
    },
    {
      title: "Active",
      field: "active",
      type: "boolean",
      default: true,
      defaultSort: "desc",
    },
  ];
  if (user?.permissions?.customers?.show_received_location_and_status) {
    columns.splice(columns.length - 1, 0, {
      title: "Geo Location",
      field: "geo_location_status",
      render: (rowData) => (
        <GeolocationStatusIndicator status={rowData.geo_location_status} />
      ),
      lookup: {
        auto: t("Yellow"),
        manual: t("Green"),
        error: t("Red"),
      },
      customSort: (a, b) => {
        const getPriority = (status) => {
          switch (status) {
            case 'manual': return 1;
            case 'auto': return 2;
            case 'error': return 3;
            default: return 4;
          }
        };
        return getPriority(a.geo_location_status) - getPriority(b.geo_location_status);
      },
    });
  }
  return columns;
};

export const PAST_DELIVERIES_TABLE_COLUMNS = (t) => {
  return [
    { title: "Customer name", field: "name", defaultSort: "asc" },
    { title: "Customer ID", field: "number" },
    { title: "Zipcode", field: "zipcode" },
    { title: "City", field: "city" },
    {
      title: "Express Customer",
      field: "priority",
      type: "boolean",
    },
    // { title: "Tour", field: "Tour.name" },
  ];
};

export const ORDERS_TABLE_COLUMNS = (checkChangeHandler, t, user) => {
  const cols = [
    {
      title: "Order id",
      render: (rowData) => (
        <span style={{ fontSize: "15px", fontWeight: 500 }}>T{rowData.Tour.user_defined_tour_id}</span>
      ),
    },
    {
      title: "Tour",
      field: "Tour.name",
      render: (rowData) =>
        rowData.Tour.name + (rowData.orders[0].departure ? ` (${t(rowData.orders[0].departure)})` : ""),
    },
    {
      title: "",
      render: (rowData) => (
        <div>
          <span style={{ color: "#6F9CEB" }}>
            {" "}
            {rowData.orders.length}
            <span style={{ marginLeft: "15px" }}>
              { } {t("New orders")}
            </span>
          </span>
          {rowData.orders.some((order) => isHandoverOrder(order, user)) && (
            <div style={{ display: "flex", alignItems: "center", marginTop: "4px", color: "#F5F5F5" }}>
              <HandshakeIcon style={{ fontSize: "16px", marginRight: "6px" }} />
              <span>{t("Handover Orders")}</span>
            </div>
          )}
        </div>
      ),
    },
  ];

  // if (
  //   user?.permissions?.routesCreateForDriver ||
  //   user?.permissions?.routes?.create_driver ||
  //   user?.permissions?.routes?.create_delivery ||
  //   user?.permissions?.routesCreateDeliveryOrder ||
  //   user?.permissions?.handovers?.create ||
  //   user?.email.startsWith('lager@')
  // ) {
  cols.push({
    title: "Id+1",
    render: (rowData) => {
      const canSelect = rowData.orders.every(
        // TODO: Adding temporary patch for AW-495, AW-495 needs to be fixed
        (order) =>
          (user?.permissions?.orders?.show_supplier ||
            user?.permissions?.customers?.list ||
            (user?.permissions?.handovers?.create && user?.permissions?.orders?.show_supplier)) &&
          ((user?.permissions?.handovers?.create || user?.permissions?.routes?.show_route_button) &&
            order?.supplier_id === user?.supplier_id &&
            order?.supplier_group_id === user?.supplier_group_id &&
            order?.supplier_location_id === user?.supplier_location_id)) || user?.permissions?.orders?.allow_changes_supplier_group_level;

      const hasAnyAssignedRoute = rowData.orders.some(
        (order) => order?.route_id != null || order?.route?.id != null || order?.Route?.id != null
      );
      const isDisabled = rowData.orders.some((order) => order?.Customer?.geo_location_status === 'error');

      if (!canSelect || hasAnyAssignedRoute) {
        // If not all orders belong to the user, don't show the checkbox
        return null;
      }

      return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
          <div style={{ textAlign: "right", marginRight: "20px", marginTop: "6px" }}>
            <input
              onChange={(e) => checkChangeHandler(e, rowData)}
              className={"radio-checkbox"}
              id={`panel${rowData.id}`}
              type="checkbox"
              name="field"
              checked={rowData.mainCheck}
              disabled={isDisabled}
            />
            <label className={isDisabled ? "" : "radio-checkbox-label"} htmlFor={`panel${rowData.id}`}>
              {rowData.mainCheck ? (
                <FilledCircleIcon />
              ) : (
                <EmptyCircleIcon style={{ color: isDisabled ? "#8a8a8a" : undefined, cursor: isDisabled ? "not-allowed" : "pointer" }} />
              )}
            </label>
          </div>
          <div style={{ display: isDisabled ? "block" : "none", marginTop: "0.5rem" }}>
            <ErrorIcon style={{ color: 'red' }} titleAccess={t("Customer address is invalid. Please review and correct the address.")} />
          </div>
        </div>
      );
    },
  });
  // }

  return cols;
};

export const CURRENT_TOURS_COLUMNS_CURRENT_TOURS_TAB = (
  tableRef,
  markFavourite,
  redirectView,
  t,
  handleUndo,
  sorting,
  user,
  handleEditRoute = null,
  handleDeviceInfo = null
) => {
  const dispatch = useDispatch();
  return [
    {
      title: "icon",
      render: (rowData) => (
        <div style={{ display: "flex" }}>
          {!!markFavourite && (
            <StarRateIcon
              onClick={(e) => markFavourite(e, rowData)}
              style={{
                color: rowData.is_favourite ? "#6F9CEB" : "#ADADAD",
                cursor: "pointer",
              }}
            />
          )}
          {!!rowData.code && user?.permissions?.routes?.map && (
            <MapIcon onClick={() => redirectView(null, rowData)} style={{ color: "#ADADAD", cursor: "pointer" }} />
          )}
          {rowData.pathway_generated_by === 'offline' && (
            <div style={{ width: "8px", height: "8px", backgroundColor: "red", borderRadius: "50%", marginLeft: "4px" }} />
          )}
          {rowData.device_info && handleDeviceInfo && user?.permissions?.routes?.driver_device_info && (
            <PhoneAndroidIcon
              onClick={() => handleDeviceInfo(rowData.device_info)}
              style={{ color: "#ADADAD", cursor: "pointer", marginLeft: "8px" }}
            />
          )}
          {rowData.has_planning_report && user?.permissions?.reports?.route_report && (
            <Tooltip title={t("Download route planning report")} placement="top" arrow>
              <IconButton
                component="a"
                href={`${import.meta.env.VITE_API}routes/${rowData.id}/export/planning-report/de?taira=${localStorage.getItem("token")}`}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                style={{ padding: 0, marginLeft: "4px" }}
                download
              >
                <AnalyticsOutlinedIcon style={{ color: "#6F9CEB" }} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
      customFilterAndSearch: (v, row) => {
        const regex = new RegExp(v, "gi");

        return regex.test(`t.${row.uuid || ""} ${row.uuid || ""} ${row.Tour?.name || ""}`);
      },
    },
    {
      title: "date",
      render: (rowData) => {
        if (!rowData.start_date) {
          if (!rowData?.created_at) {
            return "-";
          }
        }

        return (
          <>
            <span
              style={{
                font: "normal normal bold 18px/24px Roboto",
                color: "#F5F5F5",
              }}
            >
              {rowData.start_date
                ? moment(rowData.start_date).format("DD.MM.YYYY")
                : moment(rowData.created_at).format("DD.MM.YYYY")}{" "}
              {rowData.start_date ? moment(rowData.start_date).format("HH:mm") : ""}
            </span>
          </>
        );
      },
    },
    {
      title: "tour",
      field: "tour",
      render: (rowData) => (
        <span style={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
          T.{rowData.uuid}
          {rowData.vrp_info ? (
            <span style={{ marginLeft: 6, fontSize: 11, color: "#6F9CEB" }}>VRP</span>
          ) : null}
          {rowData.has_awaited_returns && (
            <Tooltip title={<TooltipBar name={"awaited_returns"} rowData={rowData} />} placement="top" arrow interactive>
              <ErrorIcon style={{ color: "red", fontSize: 18, marginLeft: 6, cursor: "pointer", verticalAlign: "middle" }} />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: "name",
      render: (rowData) => {
        try {
          const routeTiming = rowData.delivery_timing_config?.delivery_timing || null;
          let fallbackDeparture = null;
          for (const stop of rowData.pathway ?? []) {
            for (const order of stop?.Orders ?? []) {
              if (order?.departure) {
                fallbackDeparture = order.departure;
                break;
              }
            }
            if (fallbackDeparture) break;
          }
          const labelTiming = routeTiming || fallbackDeparture;
          const gdo =
            rowData.Tour.name +
            (labelTiming ? ` (${t(labelTiming)})` : "");

          return gdo;
        } catch (e) {
          console.error(e);
          return rowData.Tour.name;
        }
      },
    },
    {
      title: "progress",
      render: (rowData) => {
        if (rowData.progress === "Complete") {
          return (
            <span
              style={{
                color: "#6F9CEB",
                font: "normal normal normal 18px/24px Roboto",
              }}
            >
              {t(rowData.progress)}
              <small style={{ display: "block" }}>{moment(rowData.end_date).format("DD.MM.YYYY")}</small>
            </span>
          );
        }

        return (
          <span
            style={{
              font: "normal normal normal 18px/24px Roboto",
              color: "#F5F5F5",
            }}
          >
            {t(rowData.progress)}
          </span>
        );
      },
    },
    {
      title: "noOfOrders",
      render: (rowData) =>
        formatRouteStopCount(rowData),
    },
    {
      title: "DriversName",
      field: "driver_name",
      render: (rowData) => {
        const driverName = rowData.driver_name || "-";
        return (
          <DriverNameTooltip
            title={driverName !== "-" ? driverName : ""}
            placement="top"
            arrow
          >
            <span
              style={{
                font: "normal normal bold 18px/24px Roboto",
                color: "#F5F5F5",
                whiteSpace: "nowrap",
              }}
            >
              {driverName}
            </span>
          </DriverNameTooltip>
        );
      },
    },
    {
      title: "call",
      render: (rowData) => {
        return (
          <>
            {rowData.driver_name !== null ? (
              <Tooltip title={<TooltipBar name={"callicon"} rowData={rowData} />} placement="top" arrow interactive>
                <CallSharpIcon className={"hovericon"} />
              </Tooltip>
            ) : (
              <CallSharpIcon className={"disabled-btn"} disabled={true} />
            )}
          </>
        );
      },
    },
    canShowRouteUndoButton(user)
      ? {
        title: "undo",
        render: (rowData) => {
          return (
            <>
              {rowData.start_date == null ? (
                <IconButton onClick={() => handleUndo(rowData.id)} color="primary" style={{ padding: 0 }}>
                  <UndoSharp className={"hovericon"} />
                </IconButton>
              ) : (
                <>
                  <UndoSharp className={"disabled-btn"} />
                </>
              )}
            </>
          );
        },
      }
      : null,
    !user?.permissions?.routes?.hide_pdf || !user?.permissions?.routes?.hide_key
      ? {
        title: "key",
        render: (rowData) => {
          if (!rowData.code && !user?.permissions?.routes?.hide_pdf && !shouldHidePdf(rowData)) {
            return (
              <IconButton
                component="a"
                href={`${import.meta.env.VITE_API}routes/document/${rowData.id
                  }/${sorting}?taira=${localStorage.getItem("token")}`}
                target="_blank"
                color="primary"
                style={{ padding: 0 }}
              >
                <DocumentIcon style={{ color: "#6F9CEB" }} />
              </IconButton >
            );
          }
          return !user?.permissions?.routes?.hide_key ? (
            <Tooltip title={<TooltipBar name={"vpnicon"} rowData={rowData} />} placement="top" arrow interactive>
              <VpnKeySharpIcon className={"hovericon"} />
            </Tooltip>
          ) : null;
        },
      }
      : null,
    user?.permissions?.routes?.update
      ? {
        title: "edit",
        render: (rowData) => {
          const routeType =
            rowData?.pathway?.[0]?.tour_type_category ??
            rowData?.pathway?.find?.((stop) => stop?.tour_type_category)?.tour_type_category;
          const isEditDisabled = !!rowData?.start_date && routeType === "delivery";
          return (
            <Tooltip title={t("View Delivery Details")} placement="top" arrow>
              <span>
                <IconButton
                  onClick={() => handleEditRoute(rowData)}
                  disabled={isEditDisabled}
                  color="primary"
                  style={{ padding: 0 }}
                >
                  <EditIcon className={"hovericon"} />
                </IconButton>
              </span>
            </Tooltip>
          );
        }
      }
      : null,
  ].filter(Boolean);
};
export const CURRENT_TOURS_COLUMNS_CURRENT_TOURS_TAB_BOTH_KEY_PDF = (
  tableRef,
  markFavourite,
  redirectView,
  t,
  handleUndo,
  sorting,
  user,
  handleEditRoute = null,
  handleDeviceInfo = null
) => {
  const dispatch = useDispatch();
  let isDocumentVisible = false;
  return [
    {
      title: "icon",
      render: (rowData) => (
        <div style={{ display: "flex" }}>
          {!!markFavourite && (
            <StarRateIcon
              onClick={(e) => markFavourite(e, rowData)}
              style={{
                color: rowData.is_favourite ? "#6F9CEB" : "#ADADAD",
                cursor: "pointer",
              }}
            />
          )}
          {!!rowData.code && user?.permissions?.routes?.map && (
            <MapIcon onClick={() => redirectView(null, rowData)} style={{ color: "#ADADAD", cursor: "pointer" }} />
          )}
          {
            rowData.pathway_generated_by === 'offline' && (
              <div style={{ position: "relative" }}>
                <div style={{ width: ".5rem", height: ".5rem", backgroundColor: "red", borderRadius: "50%", marginLeft: ".5rem", position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />

              </div>
            )}

          {rowData.device_info && handleDeviceInfo && user?.permissions?.routes?.driver_device_info && (
            <PhoneAndroidIcon
              onClick={() => handleDeviceInfo(rowData.device_info)}
              style={{ color: "#ADADAD", cursor: "pointer", marginLeft: "8px" }}
            />
          )
          }
          {rowData.has_planning_report && user?.permissions?.reports?.route_report && (
            <Tooltip title={t("Download route planning report")} placement="top" arrow>
              <IconButton
                component="a"
                href={`${import.meta.env.VITE_API}routes/${rowData.id}/export/planning-report/de?taira=${localStorage.getItem("token")}`}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                style={{ padding: 0, marginLeft: "4px" }}
                download
              >
                <AnalyticsOutlinedIcon style={{ color: "#6F9CEB" }} />
              </IconButton>
            </Tooltip>
          )}
        </div >
      ),
      customFilterAndSearch: (v, row) => {
        const regex = new RegExp(v, "gi");

        return regex.test(`t.${row.uuid || ""} ${row.uuid || ""} ${row.Tour?.name || ""}`);
      },
    },
    {
      title: "date",
      render: (rowData) => {
        if (!rowData.start_date) {
          if (!rowData?.created_at) {
            return "-";
          }
        }

        return (
          <>
            <span
              style={{
                font: "normal normal bold 18px/24px Roboto",
                color: "#F5F5F5",
              }}
            >
              {rowData.start_date
                ? moment(rowData.start_date).format("DD.MM.YYYY")
                : moment(rowData.created_at).format("DD.MM.YYYY")}{" "}
              {rowData.start_date ? moment(rowData.start_date).format("HH:mm") : ""}
            </span>
          </>
        );
      },
    },
    {
      title: "tour",
      field: "tour",
      render: (rowData) => (
        <span style={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
          T.{rowData.uuid}
          {rowData.vrp_info ? (
            <span style={{ marginLeft: 6, fontSize: 11, color: "#6F9CEB" }}>VRP</span>
          ) : null}
          {rowData.has_awaited_returns && (
            <Tooltip title={<TooltipBar name={"awaited_returns"} rowData={rowData} />} placement="top" arrow interactive>
              <ErrorIcon style={{ color: "red", fontSize: 18, marginLeft: 6, cursor: "pointer", verticalAlign: "middle" }} />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: "name",
      render: (rowData) => {
        try {
          const routeTiming = rowData.delivery_timing_config?.delivery_timing || null;
          let fallbackDeparture = null;
          for (const stop of rowData.pathway ?? []) {
            for (const order of stop?.Orders ?? []) {
              if (order?.departure) {
                fallbackDeparture = order.departure;
                break;
              }
            }
            if (fallbackDeparture) break;
          }
          const labelTiming = routeTiming || fallbackDeparture;
          const gdo =
            rowData.Tour.name +
            (labelTiming ? ` (${t(labelTiming)})` : "");

          return gdo;
        } catch (e) {
          console.error(e);
          return rowData.Tour.name;
        }
      },
    },
    {
      title: "progress",
      render: (rowData) => {
        if (rowData.progress === "Complete") {
          return (
            <span
              style={{
                color: "#6F9CEB",
                font: "normal normal normal 18px/24px Roboto",
              }}
            >
              {t(rowData.progress)}
              <small style={{ display: "block" }}>{moment(rowData.end_date).format("DD.MM.YYYY")}</small>
            </span>
          );
        }

        return (
          <span
            style={{
              font: "normal normal normal 18px/24px Roboto",
              color: "#F5F5F5",
            }}
          >
            {t(rowData.progress)}
          </span>
        );
      },
    },
    {
      title: "noOfOrders",
      render: (rowData) =>
        formatRouteStopCount(rowData),
    },
    {
      title: "DriversName",
      field: "driver_name",
      render: (rowData) => {
        const driverName = rowData.driver_name || "-";
        return (
          <DriverNameTooltip
            title={driverName !== "-" ? driverName : ""}
            placement="top"
            arrow
          >
            <span
              style={{
                font: "normal normal bold 18px/24px Roboto",
                color: "#F5F5F5",
                whiteSpace: "nowrap",
              }}
            >
              {driverName}
            </span>
          </DriverNameTooltip>
        );
      },
    },
    {
      title: "call",
      render: (rowData) => {
        return (
          <>
            {rowData.driver_name !== null ? (
              <Tooltip title={<TooltipBar name={"callicon"} rowData={rowData} />} placement="top" arrow interactive>
                <CallSharpIcon className={"hovericon"} />
              </Tooltip>
            ) : (
              <CallSharpIcon className={"disabled-btn"} disabled={true} />
            )}
          </>
        );
      },
    },
    canShowRouteUndoButton(user) && {
      title: "undo",
      render: (rowData) => {
        return (
          <>
            {rowData.start_date == null ? (
              <IconButton onClick={() => handleUndo(rowData.id)} color="primary" style={{ padding: 0 }}>
                <UndoSharp className={"hovericon"} />
              </IconButton>
            ) : (
              <>
                <UndoSharp className={"disabled-btn"} />
              </>
            )}
          </>
        );
      },
    },
    !user?.permissions?.routes?.hide_pdf && {
      title: "pdf",
      render: (rowData) => {
        if (shouldHidePdf(rowData)) return null;
        isDocumentVisible = true
        return (
          <IconButton
            component="a"
            href={`${import.meta.env.VITE_API}routes/document/${rowData.id}/${sorting}?taira=${localStorage.getItem(
              "token"
            )}`}
            target="_blank"
            color="primary"
            style={{ padding: 0 }}
          >
            <DocumentIcon style={{ color: "#6F9CEB" }} />
          </IconButton>
        );
      },
    },
    !user?.permissions?.routes?.hide_key && !isDocumentVisible && {
      title: "key",
      render: (rowData) => {
        if (!rowData.code && !shouldHidePdf(rowData)) {
          return (
            <IconButton
              component="a"
              href={`${import.meta.env.VITE_API}routes/document/${rowData.id}/${sorting}?taira=${localStorage.getItem(
                "token"
              )}`}
              target="_blank"
              color="primary"
              style={{ padding: 0 }}
            >
              <DocumentIcon style={{ color: "#6F9CEB" }} />
            </IconButton>
          );
        }
        return (
          <Tooltip title={<TooltipBar name={"vpnicon"} rowData={rowData} />} placement="top" arrow interactive>
            <VpnKeySharpIcon className={"hovericon"} />
          </Tooltip>
        );
      },
    },
    user?.permissions?.routes?.update
      ? {
        title: "edit",
        render: (rowData) => {
          const routeType =
            rowData?.pathway?.[0]?.tour_type_category ??
            rowData?.pathway?.find?.((stop) => stop?.tour_type_category)?.tour_type_category;
          const isEditDisabled = !!rowData?.start_date && routeType === "delivery";
          return (
            <Tooltip title={t("View Delivery Details")} placement="top" arrow>
              <span>
                <IconButton
                  onClick={() => handleEditRoute(rowData)}
                  disabled={isEditDisabled}
                  color="primary"
                  style={{ padding: 0 }}
                >
                  <EditIcon className={"hovericon"} />
                </IconButton>
              </span>
            </Tooltip>
          );
        }
      }
      : null,
  ].filter(Boolean);
};
export const CURRENT_TOURS_COLUMNS = (tableRef, markFavourite, redirectView, t, user, handleDeviceInfo = null) => {
  return [
    {
      title: "icon",
      render: (rowData) => (
        <div style={{ display: "flex" }}>
          {!!markFavourite && (
            <StarRateIcon
              onClick={(e) => markFavourite(e, rowData)}
              style={{
                color: rowData.is_favourite ? "#6F9CEB" : "#ADADAD",
                cursor: "pointer",
              }}
            />
          )}
          {!!rowData.code && user?.permissions?.routes?.map && (
            <MapIcon onClick={() => redirectView(null, rowData)} style={{ color: "#ADADAD", cursor: "pointer" }} />
          )}
          {rowData.device_info && handleDeviceInfo && user?.permissions?.routes?.driver_device_info && (
            <PhoneAndroidIcon
              onClick={() => handleDeviceInfo(rowData.device_info)}
              style={{ color: "#ADADAD", cursor: "pointer", marginLeft: "8px" }}
            />
          )}
          {rowData.has_planning_report && user?.permissions?.reports?.route_report && (
            <Tooltip title={t("Download route planning report")} placement="top" arrow>
              <IconButton
                component="a"
                href={`${import.meta.env.VITE_API}routes/${rowData.id}/export/planning-report/de?taira=${localStorage.getItem("token")}`}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                style={{ padding: 0, marginLeft: "4px" }}
                download
              >
                <AnalyticsOutlinedIcon style={{ color: "#6F9CEB" }} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
      customFilterAndSearch: (v, row) => {
        const regex = new RegExp(v, "gi");

        return regex.test(`t.${row.uuid || ""} ${row.uuid || ""} ${row.Tour?.name || ""}`);
      },
    },
    {
      title: "date",
      render: (rowData) => {
        if (!rowData.start_date) {
          if (!rowData?.created_at) {
            return "-";
          }
        }

        return (
          <>
            <span
              style={{
                font: "normal normal bold 18px/24px Roboto",
                color: "#F5F5F5",
              }}
            >
              {rowData.start_date
                ? moment(rowData.start_date).format("DD.MM.YYYY")
                : moment(rowData.created_at).format("DD.MM.YYYY")}{" "}
              {rowData.start_date ? moment(rowData.start_date).format("HH:mm") : ""}
            </span>
          </>
        );
      },
    },
    {
      title: "tour",
      field: "tour",
      render: (rowData) => (
        <span style={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
          T.{rowData.uuid}
          {rowData.vrp_info ? (
            <span style={{ marginLeft: 6, fontSize: 11, color: "#6F9CEB" }}>VRP</span>
          ) : null}
          {rowData.has_awaited_returns && (
            <Tooltip title={<TooltipBar name={"awaited_returns"} rowData={rowData} />} placement="top" arrow interactive>
              <ErrorIcon style={{ color: "red", fontSize: 18, marginLeft: 6, cursor: "pointer", verticalAlign: "middle" }} />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: "name",
      render: (rowData) => {
        try {
          const routeTiming = rowData.delivery_timing_config?.delivery_timing || null;
          let fallbackDeparture = null;
          for (const stop of rowData.pathway ?? []) {
            for (const order of stop?.Orders ?? []) {
              if (order?.departure) {
                fallbackDeparture = order.departure;
                break;
              }
            }
            if (fallbackDeparture) break;
          }
          const labelTiming = routeTiming || fallbackDeparture;
          const gdo =
            rowData.Tour.name +
            (labelTiming ? ` (${t(labelTiming)})` : "");
          return gdo;
        } catch (e) {
          console.error(e);
          return rowData.Tour.name;
        }
      },
    },
    {
      title: "progress",
      render: (rowData) => {
        if (rowData.progress === "Completed") {
          return (
            <span
              style={{
                color: "#6F9CEB",
                font: "normal normal normal 18px/24px Roboto",
              }}
            >
              {t(rowData.progress)}
              <small style={{ display: "block" }}>{moment(rowData.end_date).format("DD.MM.YYYY")}</small>
            </span>
          );
        }

        return (
          <span
            style={{
              font: "normal normal normal 18px/24px Roboto",
              color: "#F5F5F5",
            }}
          >
            {t(rowData.progress)}
          </span>
        );
      },
    },
    {
      title: "noOfOrders",
      render: (rowData) =>
        formatRouteStopCount(rowData),
    },
    {
      title: "DriversName",
      field: "driver_name",
      render: (rowData) => {
        const driverName = rowData.driver_name || "-";
        return (
          <DriverNameTooltip
            title={driverName !== "-" ? driverName : ""}
            placement="top"
            arrow
          >
            <span
              style={{
                font: "normal normal bold 18px/24px Roboto",
                color: "#F5F5F5",
                whiteSpace: "nowrap",
              }}
            >
              {driverName}
            </span>
          </DriverNameTooltip>
        );
      },
    },
    {
      title: "call",
      render: (rowData) => {
        return (
          <>
            {rowData.driver_name !== null ? (
              <Tooltip title={<TooltipBar name={"callicon"} rowData={rowData} />} placement="top" arrow interactive>
                <CallSharpIcon className={"hovericon"} />
              </Tooltip>
            ) : (
              <CallSharpIcon className={"disabled-btn"} disabled={true} />
            )}
          </>
        );
      },
    },
    !user?.permissions?.routes?.hide_pdf || !user?.permissions?.routes?.hide_key
      ? {
        title: "key",
        render: (rowData) => {
          if (!rowData.code && !user?.permissions?.routes?.hide_pdf && !shouldHidePdf(rowData)) {
            return (
              <IconButton
                component="a"
                href={`${import.meta.env.VITE_API}routes/document/${rowData.id}?taira=${localStorage.getItem(
                  "token"
                )}`}
                target="_blank"
                color="primary"
                style={{ padding: 0 }}
              >
                <DocumentIcon style={{ color: "#6F9CEB" }} />
              </IconButton>
            );
          }

          return !user?.permissions?.routes?.hide_key ? (
            <Tooltip title={<TooltipBar name={"vpnicon"} rowData={rowData} />} placement="top" arrow interactive>
              <VpnKeySharpIcon className={"hovericon"} />
            </Tooltip>
          ) : null;
        },
      }
      : null,
  ];
};
export const RECENTLY_FINISHED_ROUTES_COLUMNS = (tableRef, markFavourite, redirectView, t, sorting, user, handleDeviceInfo = null) => {
  let isDocumentVisible = false
  let isKeyVisible = false
  return [
    {
      title: "icon",
      render: (rowData) => (
        <div style={{ display: "flex" }}>
          {!!markFavourite && (
            <StarRateIcon
              onClick={(e) => markFavourite(e, rowData)}
              style={{
                color: rowData.is_favourite ? "#6F9CEB" : "#ADADAD",
                cursor: "pointer",
              }}
            />
          )}
          {!!rowData.code && user?.permissions?.routes?.map && (
            <MapIcon onClick={() => redirectView(null, rowData)} style={{ color: "#ADADAD", cursor: "pointer" }} />
          )}
          {rowData.device_info && handleDeviceInfo && user?.permissions?.routes?.driver_device_info && (
            <PhoneAndroidIcon
              onClick={() => handleDeviceInfo(rowData.device_info)}
              style={{ color: "#ADADAD", cursor: "pointer", marginLeft: "8px" }}
            />
          )}
          {rowData.has_planning_report && user?.permissions?.reports?.route_report && (
            <Tooltip title={t("Download route planning report")} placement="top" arrow>
              <IconButton
                component="a"
                href={`${import.meta.env.VITE_API}routes/${rowData.id}/export/planning-report/de?taira=${localStorage.getItem("token")}`}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                style={{ padding: 0, marginLeft: "4px" }}
                download
              >
                <AnalyticsOutlinedIcon style={{ color: "#6F9CEB" }} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
      customFilterAndSearch: (v, row) => {
        const regex = new RegExp(v, "gi");

        return regex.test(`t.${row.uuid || ""} ${row.uuid || ""} ${row.Tour?.name || ""}`);
      },
    },
    {
      title: "date",
      render: (rowData) => {
        if (!rowData.start_date) {
          if (!rowData?.created_at) {
            return "-";
          }
        }

        return (
          <>
            <span
              style={{
                font: "normal normal bold 18px/24px Roboto",
                color: "#F5F5F5",
              }}
            >
              {rowData.start_date
                ? moment(rowData.start_date).format("DD.MM.YYYY")
                : moment(rowData.created_at).format("DD.MM.YYYY")}{" "}
              {rowData.start_date ? moment(rowData.start_date).format("HH:mm") : ""}
            </span>
          </>
        );
      },
    },
    {
      title: "tour",
      field: "tour",
      render: (rowData) => (
        <span>
          T.{rowData.uuid}
          {rowData.vrp_info ? (
            <span style={{ marginLeft: 6, fontSize: 11, color: "#6F9CEB" }}>VRP</span>
          ) : null}
        </span>
      ),
    },
    {
      title: "name",
      render: (rowData) => {
        try {
          const routeTiming = rowData.delivery_timing_config?.delivery_timing || null;
          let fallbackDeparture = null;
          for (const stop of rowData.pathway ?? []) {
            for (const order of stop?.Orders ?? []) {
              if (order?.departure) {
                fallbackDeparture = order.departure;
                break;
              }
            }
            if (fallbackDeparture) break;
          }
          const labelTiming = routeTiming || fallbackDeparture;
          const gdo =
            rowData.Tour.name +
            (labelTiming ? ` (${t(labelTiming)})` : "");

          return gdo;
        } catch (e) {
          console.error(e);
          return rowData.Tour.name;
        }
      },
    },
    {
      title: "progress",
      render: (rowData) => {
        if (rowData.progress === "Completed") {
          return (
            <span
              style={{
                color: "#6F9CEB",
                font: "normal normal normal 18px/24px Roboto",
              }}
            >
              {t(rowData.progress)}
              <small style={{ display: "block" }}>{moment(rowData.end_date).format("DD.MM.YYYY")}</small>
            </span>
          );
        }

        return (
          <span
            style={{
              font: "normal normal normal 18px/24px Roboto",
              color: "#F5F5F5",
            }}
          >
            {t(rowData.progress)}
          </span>
        );
      },
    },
    {
      title: "noOfOrders",
      render: (rowData) =>
        formatRouteStopCount(rowData),
    },
    {
      title: "DriversName",
      field: "driver_name",
      render: (rowData) => {
        const driverName = rowData.driver_name || "-";
        return (
          <DriverNameTooltip
            title={driverName !== "-" ? driverName : ""}
            placement="top"
            arrow
          >
            <span
              style={{
                font: "normal normal bold 18px/24px Roboto",
                color: "#F5F5F5",
                whiteSpace: "nowrap",
              }}
            >
              {driverName}
            </span>
          </DriverNameTooltip>
        );
      },
    },
    {
      title: "call",
      render: (rowData) => {
        return (
          <>
            {rowData.driver_name !== null ? (
              <Tooltip title={<TooltipBar name={"callicon"} rowData={rowData} />} placement="top" arrow interactive>
                <CallSharpIcon className={"hovericon"} />
              </Tooltip>
            ) : (
              <CallSharpIcon className={"disabled-btn"} disabled={true} />
            )}
          </>
        );
      },
    },
    !user?.permissions?.routes?.hide_pdf && {
      title: "pdf",
      render: (rowData) => {
        if (shouldHidePdf(rowData)) return null;
        isDocumentVisible = true
        return (
          <IconButton
            component="a"
            href={`${import.meta.env.VITE_API}routes/document/${rowData.id}/${sorting}?taira=${localStorage.getItem(
              "token"
            )}`}
            target="_blank"
            color="primary"
            style={{ padding: 0 }}
          >
            <DocumentIcon style={{ color: "#6F9CEB" }} />
          </IconButton>
        );
      },
    },
    !user?.permissions?.routes?.hide_key && {
      title: "key",
      render: (rowData) => {
        if (user?.account_permissions?.delivery_app_doc && !isDocumentVisible && !shouldHidePdf(rowData)) {
          return (
            <>
              <IconButton
                component="a"
                href={`${import.meta.env.VITE_API}routes/document/${rowData.id
                  }/${sorting}?taira=${localStorage.getItem("token")}`}
                target="_blank"
                color="primary"
                style={{ padding: 0 }}
              >
                <DocumentIcon style={{ color: "#6F9CEB" }} />
              </IconButton>
            </>
          );
        }
        if (!rowData.code && !user?.permissions?.routes?.hide_pdf && !isDocumentVisible && !shouldHidePdf(rowData)) {
          return (
            <IconButton
              component="a"
              href={`${import.meta.env.VITE_API}routes/document/${rowData.id}/${sorting}?taira=${localStorage.getItem(
                "token"
              )}`}
              target="_blank"
              color="primary"
              style={{ padding: 0 }}
            >
              <DocumentIcon style={{ color: "#6F9CEB" }} />
            </IconButton>
          );
        }
        isKeyVisible = true
        return (
          <Tooltip title={<TooltipBar name={"vpnicon"} rowData={rowData} />} placement="top" arrow interactive>
            <VpnKeySharpIcon className={"hovericon"} />
          </Tooltip>
        );
      },
    },
    {
      title: "key2",
      render: (rowData) => {
        if (user?.account_permissions?.delivery_app_doc && !isKeyVisible) {
          return (
            <Tooltip title={<TooltipBar name={"vpnicon"} rowData={rowData} />} placement="top" arrow interactive>
              <VpnKeySharpIcon className={"hovericon"} />
            </Tooltip>
          );
        }
      },
    },
  ].filter(Boolean);
};
export const ARCHIVED_ROUTES_COLUMNS = (tableRef, redirectView, t, user, sorting, handleDeviceInfo = null) => {
  const showBothKeyPdf = user?.permissions?.routes?.delivery_app_doc;
  let isDocumentVisible = false
  return [
    {
      title: "icon",
      render: (rowData) => (
        <div style={{ display: "flex" }}>
          {!!rowData.code && user?.permissions?.routes?.map && (
            <MapIcon onClick={() => redirectView(null, rowData)} style={{ color: "#ADADAD", cursor: "pointer" }} />
          )}
          {rowData.device_info && handleDeviceInfo && user?.permissions?.routes?.driver_device_info && (
            <PhoneAndroidIcon
              onClick={() => handleDeviceInfo(rowData.device_info)}
              style={{ color: "#ADADAD", cursor: "pointer", marginLeft: "8px" }}
            />
          )}
          {rowData.has_planning_report && user?.permissions?.reports?.route_report && (
            <Tooltip title={t("Download route planning report")} placement="top" arrow>
              <IconButton
                component="a"
                href={`${import.meta.env.VITE_API}routes/${rowData.id}/export/planning-report/de?taira=${localStorage.getItem("token")}`}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                style={{ padding: 0, marginLeft: "4px" }}
                download
              >
                <AnalyticsOutlinedIcon style={{ color: "#6F9CEB" }} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
      customFilterAndSearch: (v, row) => {
        const regex = new RegExp(v, "gi");
        return regex.test(`t.${row.uuid || ""} ${row.uuid || ""} ${row.Tour?.name || ""}`);
      },
    },
    {
      title: "date",
      render: (rowData) => {
        if (!rowData.start_date) {
          if (!rowData?.created_at) {
            return "-";
          }
        }

        return (
          <>
            <span
              style={{
                font: "normal normal bold 18px/24px Roboto",
                color: "#F5F5F5",
              }}
            >
              {rowData.start_date
                ? moment(rowData.start_date).format("DD.MM.YYYY")
                : moment(rowData.created_at).format("DD.MM.YYYY")}{" "}
              {rowData.start_date ? moment(rowData.start_date).format("HH:mm") : ""}
            </span>
          </>
        );
      },
    },
    {
      title: "tour",
      field: "tour",
      render: (rowData) => (
        <span>
          T.{rowData.uuid}
          {rowData.vrp_info ? (
            <span style={{ marginLeft: 6, fontSize: 11, color: "#6F9CEB" }}>VRP</span>
          ) : null}
        </span>
      ),
    },
    {
      title: "name",
      render: (rowData) => {
        try {
          const routeTiming = rowData.delivery_timing_config?.delivery_timing || null;
          let fallbackDeparture = null;
          for (const stop of rowData.pathway ?? []) {
            for (const order of stop?.Orders ?? []) {
              if (order?.departure) {
                fallbackDeparture = order.departure;
                break;
              }
            }
            if (fallbackDeparture) break;
          }
          const labelTiming = routeTiming || fallbackDeparture;
          const gdo =
            rowData.Tour.name +
            (labelTiming ? ` (${t(labelTiming)})` : "");

          return gdo;
        } catch (e) {
          console.error(e);
          return rowData.Tour.name;
        }
      },
    },
    {
      title: "progress",
      render: (rowData) => {
        if (rowData.progress === "Complete") {
          return (
            <span
              style={{
                color: "#6F9CEB",
                font: "normal normal normal 18px/24px Roboto",
              }}
            >
              {t(rowData.progress)}
              <small style={{ display: "block" }}>{moment(rowData.end_date).format("DD.MM.YYYY")}</small>
            </span>
          );
        }

        return (
          <span
            style={{
              font: "normal normal normal 18px/24px Roboto",
              color: "#F5F5F5",
            }}
          >
            {t(rowData.progress)}
          </span>
        );
      },
    },
    {
      title: "noOfOrders",
      render: (rowData) =>
        formatRouteStopCount(rowData),
    },
    {
      title: "DriversName",
      field: "driver_name",
      render: (rowData) => {
        const driverName = rowData.driver_name || "-";
        return (
          <DriverNameTooltip
            title={driverName !== "-" ? driverName : ""}
            placement="top"
            arrow
          >
            <span
              style={{
                font: "normal normal bold 18px/24px Roboto",
                color: "#F5F5F5",
                whiteSpace: "nowrap",
              }}
            >
              {driverName}
            </span>
          </DriverNameTooltip>
        );
      },
    },
    {
      title: "call",
      render: (rowData) => {
        return (
          <>
            {rowData.driver_name !== null ? (
              <Tooltip title={<TooltipBar name={"callicon"} rowData={rowData} />} placement="top" arrow interactive>
                <CallSharpIcon className={"hovericon"} />
              </Tooltip>
            ) : (
              <CallSharpIcon className={"disabled-btn"} disabled={true} />
            )}
          </>
        );
      },
    },
    (showBothKeyPdf || !user?.permissions?.routes?.hide_pdf) && {
      title: "pdf",
      render: (rowData) => {
        if (shouldHidePdf(rowData)) return null;
        isDocumentVisible = true
        return (
          <IconButton
            component="a"
            href={`${import.meta.env.VITE_API}routes/document/${rowData.id}/${sorting}?taira=${localStorage.getItem(
              "token"
            )}`}
            target="_blank"
            color="primary"
            style={{ padding: 0 }}
          >
            <DocumentIcon style={{ color: "#6F9CEB" }} />
          </IconButton>
        );
      },
    },
    (showBothKeyPdf || !user?.permissions?.routes?.hide_key) && {
      title: "key",
      render: (rowData) => {
        if (showBothKeyPdf) {
          return (
            <Tooltip title={<TooltipBar name={"vpnicon"} rowData={rowData} />} placement="top" arrow interactive>
              <VpnKeySharpIcon className={"hovericon"} />
            </Tooltip>
          );
        } else {
          if (!rowData.code && !user?.permissions?.routes?.hide_pdf && !isDocumentVisible && !shouldHidePdf(rowData)) {
            return (
              <IconButton
                component="a"
                href={`${import.meta.env.VITE_API}routes/document/${rowData.id}/${sorting}?taira=${localStorage.getItem(
                  "token"
                )}`}
                target="_blank"
                color="primary"
                style={{ padding: 0 }}
              >
                <DocumentIcon style={{ color: "#6F9CEB" }} />
              </IconButton>
            );
          }
          return !user?.permissions?.routes?.hide_key ? (
            <Tooltip title={<TooltipBar name={"vpnicon"} rowData={rowData} />} placement="top" arrow interactive>
              <VpnKeySharpIcon className={"hovericon"} />
            </Tooltip>
          ) : null;
        }
      },
    },
  ].filter(Boolean);
};

export const checkPaths = [
  PATHS.tours.root,
  PATHS.tours.add,
  PATHS.tours.edit,
  PATHS.tours.detail,
  PATHS.customers.root,
  PATHS.customers.archive,
  PATHS.customers.add,
  PATHS.customers.edit,
  PATHS.customers.detail,
  PATHS.customers.view,
  PATHS.customers.addWithExistingData,
  PATHS.zipcodes.root,
  PATHS.zipcodes.add,
  PATHS.zipcodes.edit,
  PATHS.zipcodes.visualization,
];
export const checkRoutePaths = [
  PATHS.routes.current,
  PATHS.routes.recent,
  PATHS.routes.archive,
  PATHS.routes.export,
  PATHS.routes.mapRecent,
  PATHS.routes.mapArchive,
];

export const FEEDBACKS_TABLE_COLUMNS = (t) => {
  return [
    { title: "Id", field: "id" },
    { title: "Feedback Type", field: "type", render: (rowData) => t(rowData.type) },
    { title: "User", field: "user_name" },
    { title: "E-Mail", field: "user_email" },
    { title: "Supplier Location", field: "supplier_location_name" },
    {
      title: "Created",
      field: "created_at",
      render: (rowData) => (rowData.created_at ? moment(rowData.created_at).format("DD.MM.YYYY HH:mm") : "-"),
    },
    { title: "Email Sent", field: "email_sent", type: "boolean" },
  ];
};

export const MOBILE_LOGS_TABLE_COLUMNS = (t) => {
  return [
    { title: "Id", field: "id" },
    { title: "Route ID", field: "route_id" },
    { title: "Event", field: "event_type" },
    { title: "Level", field: "level" },
    {
      title: "Payload",
      field: "payload",
      render: (rowData) => (
        <span
          style={{
            maxWidth: "360px",
            display: "inline-block",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {rowData.payload}
        </span>
      ),
    },
    {
      title: "Client Time",
      field: "client_timestamp",
      render: (rowData) =>
        rowData.client_timestamp ? moment(rowData.client_timestamp).format("DD.MM.YYYY HH:mm:ss") : "-",
    },
    {
      title: "Created",
      field: "created_at",
      render: (rowData) => (rowData.created_at ? moment(rowData.created_at).format("DD.MM.YYYY HH:mm:ss") : "-"),
    },
  ];
};

export const MOBILE_LOG_ROUTES_TABLE_COLUMNS = (t) => {
  return [
    { title: "Route ID", field: "route_id" },
    { title: "Logs", field: "log_count" },
    { title: "Latest Level", field: "latest_level" },
    { title: "Latest Event", field: "latest_event_type" },
    {
      title: "Last Activity",
      field: "latest_created_at",
      render: (rowData) =>
        rowData.latest_created_at
          ? moment(rowData.latest_created_at).format("DD.MM.YYYY HH:mm:ss")
          : "-",
    },
  ];
};
